import { useContext, useLayoutEffect, useMemo, useState } from "react";
import { StatusRow } from "./GeneralLedgerGroupList";
import CustomizerContext from "../../../../../_helper/Customizer";
import {
    BalanceType,
    Code,
    CreatedBy,
    Description,
    GeneralLedgerGroup,
    GroupName,
    ModifiedBy,
    Report,
    Status,
} from "../../../../../Constant";
import {
    financesGeneralLedgerGroupApi,
    financesReportTypeApi,
} from "../../../../../api";
import { createDropdownField } from "../../../../../Data/FormField/PublicData";
import { IGeneralLedgerGroupResponse } from "../GeneralLedgerGroup.types";
import { toast } from "react-toastify";
import { formatDateWithTimeZone } from "../../../../../_helper/dateHelper";
import useAuth from "../../../../../Hooks/useAuth";
import useAxiosPrivate from "../../../../../Hooks/useAxiosPrivate";

export default function useGeneralLedgerGroupListController() {
    const axiosPrivate = useAxiosPrivate();
    const title = GeneralLedgerGroup;
    const { userOrganisationId }: any = useAuth();
    const [formFields, setFormFields] = useState<any[]>([
        {
            id: "name",
            name: "name",
            label: GroupName,
            type: "text",
            value: "",
            required: true,
            colSize: 6,
        },

        {
            id: "description",
            name: "description",
            label: Description,
            type: "textarea",
            value: "",
            required: true,
            colSize: 6,
        },

        {
            id: "balance_type",
            name: "balance_type",
            label: BalanceType,
            type: "radio",
            value: "",
            colSize: 6,
            options: [
                { label: "Credit", value: 1 },
                { label: "Debit", value: 0 },
            ],
        },

        {
            id: "is_active",
            name: "is_active",
            label: Status,
            type: "switch",
            value: "1",
            colSize: 6,
        },
    ]);

    const tableColumns = [
        {
            name: GroupName,
            selector: (row: (typeof data)[0]) => `${row.name}`,
            sortable: true,
            center: false,
        },
        {
            name: Code,
            selector: (row: (typeof data)[0]) => `${row.code}`,
            sortable: false,
            center: false,
        },
        {
            name: BalanceType,
            selector: (row: (typeof data)[0]) =>
                `${row.balance_type === 1 ? "Credit" : "Debit"}`,
            sortable: true,
            center: false,
        },

        {
            name: Description,
            selector: (row: (typeof data)[0]) => `${row.description}`,
            sortable: false,
            center: false,
        },
        {
            name: Report,
            selector: (row: (typeof data)[0]) => `${row.report_type.name}`,
            sortable: false,
            center: false,
        },
        {
            name: Status,
            selector: (row: (typeof data)[0]) => `${row.is_active}`,
            sortable: true,
            center: true,
            cell: (row: (typeof data)[0]) => StatusRow(row),
        },
        {
            name: CreatedBy,
            selector: (row: (typeof data)[0]) =>
                `${formatDateWithTimeZone(row.created_at)}`,
            sortable: false,
            center: false,
        },

        {
            name: ModifiedBy,
            selector: (row: (typeof data)[0]) =>
                `${formatDateWithTimeZone(row.updated_at)}`,
            sortable: false,
            center: false,
        },
    ];

    //?? DROPDOWNS INITS >
    const reportDropDown = createDropdownField(
        axiosPrivate,
        "report_type_id",
        "report_type_id",
        Report,
        `${financesReportTypeApi}?skip=${0}&limit=${10}&with_trashed=${false}&organisation_id=${userOrganisationId}`,
        "",
        6
    );
    reportDropDown.onChange = async (id, updatedFields) => {
        const updatedFormFields = updatedFields.map((field: any) => {
            if (field.id === "report_type_id") {
                return { ...field, value: id };
            }
            return field;
        });
        setFormFields([...updatedFormFields]);
    };

    useLayoutEffect(() => {
        //?? Updates all fields once component is mounted
        (async function () {
            const updatedFields = [
                reportDropDown,
                ...formFields,
                // balanceLedgerDropDown,
            ];
            // await balanceLedgerDropDown.fetchOptions();
            await reportDropDown.fetchOptions();
            setFormFields(updatedFields);
        })();
    }, []);

    function fetchData() {
        setLoading(true);
        axiosPrivate
            .get(
                `${financesGeneralLedgerGroupApi}?skip=${0}&limit=${10}&with_trashed=${false}&organisation_id=${userOrganisationId}`
            )
            .then((res) => {
                const response: IGeneralLedgerGroupResponse[] = res.data;
                setData(response);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 404) {
                    toast.error(err.response.data.detail);
                } else {
                    toast.error("Whoops! Something went wrong");
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useLayoutEffect(() => {
        fetchData();
    }, []);

    const handleEdit = (row: (typeof data)[0]) => {
        setViewModalOpen(true);
        setEditMode(true);

        // row.balance_type = row?.balance_type;
        // balanceLedgerDropDown.onChange(row.balance_type, formFields);

        // row.report = row?.ledgers?.report.id;
        reportDropDown.onChange(row.report_type_id, formFields);
        setSelectedRowData(row);
    };

    const { layoutURL } = useContext(CustomizerContext);
    const newBtn = `${process.env.PUBLIC_URL}/finance/settings/general-ledger-group/create/${layoutURL}`;
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<IGeneralLedgerGroupResponse[]>([]);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState<
        (typeof data)[0] | null
    >(null);
    const [editMode, setEditMode] = useState(false);

    const handleView = (row: (typeof data)[0]) => {
        setViewModalOpen(true);
        setEditMode(false);
        setSelectedRowData(row);
    };

    const handleEditRec = async (
        id: number | string,
        editRecord: (typeof data)[0]
    ) => {
        await axiosPrivate
            .put(`${financesGeneralLedgerGroupApi}/${id}`, {
                ...editRecord,
                organisation_id: userOrganisationId,
                balance_type: editRecord.balance_type === 1 ? true : false,
            })
            .then((_res) => {
                const updatedData = data.map((row) =>
                    row.id === selectedRowData!.id
                        ? { ...row, ..._res.data }
                        : row
                );
                setData(updatedData);
                toast.success(`${_res.data.name} Editted Successfully.`);
                //fetchData();
            })
            .catch((err) => {
                if (err.response && err.response.status === 422) {
                    toast.error("Validation Errors");
                } else if (err.response && err.response.status === 400) {
                    toast.error(err.response.data.detail);
                } else {
                    toast.error("Whoops! Something went wrong");
                }
                throw err;
                //toast.error(err.response.message || "Whoops! Something went wrong");
            });
    };

    const memoizedHandleEdit = useMemo(
        () => handleEditRec,
        [data, selectedRowData]
    );

    return {
        data,
        tableColumns,
        viewModalOpen,
        selectedRowData,
        editMode,
        newBtn,
        title,
        formFields,
        loading,
        setViewModalOpen,
        handleView,
        handleEdit,
        memoizedHandleEdit,
    };
}
